import { useEffect, useState } from 'react';
import { generateAlertTimeSeriesRequest, LRQRequest } from 'hooks/useLRQ/useLRQ';
import { useEventCache } from 'hooks/useLRQ/useEventCache';
import { AlertInfo, QueryRequestInput, QueryType } from 'common/graphqlTypes';
import LoadingPlaceholder from 'components/common/LoadingPlaceholder/LoadingPlaceholder';
import TimeSeriesGraph from 'components/alerts/AlertDetailsPage/TimeSeriesGraph';
import LRQRequestManager from 'components/common/Managers/LRQRequestManager';
import { parseDuration } from 'common/util/time';
import { getRequestId } from 'common/util/lrqRequest';
import last from 'lodash/last';
import './TimeSeriesGraphContainer.scss';

const DEFAULT_AGGREGATE_FUNCTION = 'count';
const NUMBER_OF_SLICES = 60;

type TimeSeriesGraphContainerProps = {
  alert: AlertInfo;
};

/**
 * This component is the container for the TimeSeriesGraph component. This is responsible for returning the correct data
 * for the TimeSeriesGraph to render and containing the manager for LRQ Requests
 *
 * @prop alert            The Alert
 */

function TimeSeriesGraphContainer({ alert }: TimeSeriesGraphContainerProps): JSX.Element {
  const [timeSeriesGraphRequest, setTimeSeriesGraphRequest] = useState<LRQRequest | null>(null);
  const id = getRequestId(timeSeriesGraphRequest, QueryType.PLOT);
  const { results } = useEventCache([id]);

  useEffect(() => {
    const getComplexTriggerExpression = (): string => {
      // It's possible that there can be multiple trigger expressions, however we only graph the first trigger expression.
      const firstParsedTriggerExpressions = alert.parsedTriggerExpressions[0];
      const { facet, filter, fn } = firstParsedTriggerExpressions;
      const aggregateFunction = fn.operator || DEFAULT_AGGREGATE_FUNCTION;
      return `${aggregateFunction}(${facet} where ${filter})`;
    };

    // Parses time interval from a trigger expression
    // The expression, count:5 minutes($app == "tomcat") > 0.1 would give us "5 minutes"
    const timeInterval = alert.trigger.split(':')?.[1].split('(')?.[0] || '2 minutes';
    const duration = parseDuration(timeInterval);
    const slices = 3600000 / duration;

    const request = generateAlertTimeSeriesRequest({
      startTime: '1 hour',
      endTime: '',
      plotQuery: {
        expression: getComplexTriggerExpression(),
        slices: slices >= 1 ? slices : NUMBER_OF_SLICES,
        autoAlign: false,
      },
    } as unknown as QueryRequestInput);

    setTimeSeriesGraphRequest(request);
  }, [alert]);

  if (!results) {
    return <LoadingPlaceholder />;
  }

  const series = results?.[0]?.seriesData?.series;

  const getThreshold = (): string => {
    const splitTriggerStr = alert.trigger.split(' ');
    const threshold = last(splitTriggerStr);
    return threshold ? (threshold as string) : '';
  };

  return (
    <div className="TimeSeriesGraphContainer">
      {series ? <TimeSeriesGraph data={series} threshold={getThreshold()} alert={alert} /> : null}
      {timeSeriesGraphRequest && (
        <LRQRequestManager key={timeSeriesGraphRequest.key} request={timeSeriesGraphRequest} />
      )}
    </div>
  );
}

export default TimeSeriesGraphContainer;
